import ArrowBack from '@mui/icons-material/ArrowBack'
import Book from '@mui/icons-material/Book'
import CheckBox from '@mui/icons-material/CheckBox'
import ReceiptLong from '@mui/icons-material/ReceiptLong'
import Box from '@mui/material/Box'
import LoadingButton from '@mui/material/Button'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import {
  DataGridPremium,
  GridActionsCellItem,
  type GridColDef,
  type GridFilterItem,
  type GridFilterOperator,
  getGridStringOperators,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import FeatherIcon from 'feather-icons-react'
import useMaterialNotification from 'hooks/useMaterialNotification'
import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { colors } from 'themes'
import { useAddPaymentToWhitelist, usePaymentReceiver, usePayments, useUpdatePayments } from '../api/react-query'
import { type BackOfficePaymentResponseDto, PaymentSource, PaymentState } from '../api/swagger/definitions/backoffice'
import config from '../config'
import ColorsDeprecated from '../figma/tokens/ColorsDeprecated'
import { TableHeader, useTableGridProps } from '../helpers/getTableOptions'
import { getPaymentsActions } from '../helpers/PaymentsActionsHelper'
import { formatCurrency } from '../helpers/utils'
import { TextKeys } from '../tiger/libs/TextRepository'
import JSONDetailPanel from './JSONDetailPanel'
import { CoreMyntModal } from './modals/CoreMyntModal'
import { TextFieldController } from './react-hook-components'
import TableWrapper from './TableWrapper'

const STATE = 'state'

// The default `contains` filter renders its input at `size="small"`, which is shorter than
// the header filter row's other controls (see `createStateFilterOperator` below) and looks
// clipped alongside them. Reuse the default filter logic, just with a full-size input.
// getGridStringOperators()[0] is always the 'contains' operator.
const containsFilterFn = getGridStringOperators()[0].getApplyFilterFn

const createTextFilterOperator = (label: string): GridFilterOperator => ({
  label,
  value: 'contains',
  getApplyFilterFn: containsFilterFn,
  InputComponent: ({ applyValue, ...params }) => {
    const timerRef = useRef<ReturnType<typeof setTimeout>>()
    const itemRef = useRef(params.item)
    itemRef.current = params.item

    return (
      <Box sx={{ width: '100%', paddingTop: 2 }}>
        <TextField
          fullWidth
          label={label}
          defaultValue={params.item.value ?? ''}
          onChange={(event) => {
            clearTimeout(timerRef.current)
            const value = event.target.value
            timerRef.current = setTimeout(() => applyValue({ ...itemRef.current, value }), 300)
          }}
          onKeyDown={(event) => event.stopPropagation()}
        />
      </Box>
    )
  },
  headerLabel: label,
})

const createSingleSelectFilterOperator = (label: string, options: Record<string, string>): GridFilterOperator => ({
  label,
  value: 'is',
  getApplyFilterFn: (filterItem: GridFilterItem) => {
    if (!filterItem.value) return null

    return (value: unknown) => value === filterItem.value
  },
  InputComponent: ({ applyValue, clearButton, ...params }) => {
    const selected = (params.item.value as string | undefined) ?? ''
    const labelId = `payments-filter-${params.item.field}`

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', paddingTop: 2 }}>
        <FormControl fullWidth sx={{ minWidth: 0 }}>
          <InputLabel id={labelId}>{label}</InputLabel>
          <Select
            labelId={labelId}
            label={label}
            value={selected}
            onKeyDown={(event) => event.stopPropagation()}
            onChange={(event) => applyValue({ ...params.item, value: event.target.value || undefined })}
          >
            <MenuItem value="">
              <em>Any</em>
            </MenuItem>
            {Object.values(options).map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selected ? clearButton : null}
      </Box>
    )
  },
  headerLabel: label,
})

const createStateFilterOperator = (label: string, options: Record<string, string>): GridFilterOperator => ({
  label,
  value: 'customArrayContains',
  getApplyFilterFn: (filterItem: GridFilterItem) => {
    const filter = Array.from(filterItem.value ?? [])

    if (filter.length === 0) return null

    return (value: unknown) => filter.includes(value)
  },
  InputComponent: ({ applyValue, clearButton, ...params }) => {
    const selected = (params.item.value as string[] | undefined) ?? []
    const labelId = `payments-filter-${params.item.field}`

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', paddingTop: 2 }}>
        <FormControl fullWidth sx={{ minWidth: 0 }}>
          <InputLabel id={labelId}>{label}</InputLabel>
          <Select
            labelId={labelId}
            label={label}
            multiple
            value={selected}
            onKeyDown={(event) => event.stopPropagation()}
            onChange={(event) => {
              const value = event.target.value
              applyValue({ ...params.item, value: typeof value === 'string' ? value.split(',') : value })
            }}
            renderValue={(values) => (values.length === 0 ? '' : `${values.length} selected`)}
            sx={{
              '&& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                padding: '12.5px 32px 12.5px 12.5px',
              },
            }}
          >
            {Object.values(options).map((option) => (
              <MenuItem key={option} value={option}>
                <Checkbox checked={selected.includes(option)} size="small" sx={{ pointerEvents: 'none', padding: 0, marginRight: 1 }} />
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selected.length > 0 ? clearButton : null}
      </Box>
    )
  },
  headerLabel: label,
})

const ACTION_ICONS = [<Book key="book" />, <CheckBox key="checkbox" />, <ArrowBack key="arrow_back" />, <ReceiptLong key="receipt_long" />]

export function PaymentsTable() {
  const [stateFilter, setStateFilter] = React.useState([
    PaymentState.MAPPED,
    PaymentState.INCOMING,
    PaymentState.SENDER_UNKNOWN,
    PaymentState.TO_BE_RETURNED,
  ])

  const [selectedPayment, setSelectedPayment] = useState<BackOfficePaymentResponseDto>()
  const [loading, setIsLoading] = useState(false)

  const [sender, setSender] = useState<BackOfficePaymentResponseDto | undefined>()

  const notify = useMaterialNotification()

  const { data: payments = [] } = usePayments(stateFilter)
  const mutation = useUpdatePayments()

  const addToWhiteList = useAddPaymentToWhitelist()

  const form = useForm({
    defaultValues: {
      ocr: '',
    },
  })

  const gridProps = useTableGridProps({ headerTextKey: TextKeys.accountingPaymentsOCR })

  const apiRef = useGridApiRef()

  const handleModalClose = () => {
    setSelectedPayment(undefined)
    form.reset({ ocr: '' })
  }

  const handleAddOcr = ({ ocr }) => {
    if (!selectedPayment) return

    setIsLoading(true)

    mutation
      .mutateAsync({
        paymentId: selectedPayment.id,
        payout: {
          ocr,
        },
      })
      .then(() => {
        notify(`Updated payment with OCR ${ocr}`)
        setSelectedPayment(undefined)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore existing biome errors
  useEffect(() => {
    if (selectedPayment) {
      // If the payment has an OCR use that
      const ocrValue = selectedPayment.ocr ?? ''

      form.setValue('ocr', ocrValue)
    }
  }, [selectedPayment])

  const handleAddToWhitelist = (payment: BackOfficePaymentResponseDto) => addToWhiteList.mutateAsync({ paymentId: payment.id })

  const onReturnMoney = (payment: BackOfficePaymentResponseDto) =>
    mutation.mutateAsync({
      paymentId: payment.id,
      payout: {
        state: PaymentState.TO_BE_RETURNED,
      },
    })

  const actionFactories = getPaymentsActions({ mutation: mutation as any, onVerifySender: (payment) => setSender(payment) })

  const columns: GridColDef<BackOfficePaymentResponseDto>[] = [
    {
      headerName: 'OCR',
      field: 'ocr',
      flex: 1,
      filterOperators: [createTextFilterOperator('OCR')],
      renderCell: ({ row }) => {
        const ocr = row.ocr ?? 'Add OCR'

        return (
          <Box
            onClick={() => setSelectedPayment(row)}
            sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: colors.product.blue[600], cursor: 'pointer' }}
          >
            <span>{ocr}</span>
            <FeatherIcon size={16} icon="edit-2" />
          </Box>
        )
      },
    },
    {
      headerName: 'State',
      field: STATE,
      flex: 1,
      filterOperators: [createStateFilterOperator('State', PaymentState)],
    },
    {
      headerName: 'Date',
      field: 'paymentDate',
      flex: 1,
      filterOperators: [createTextFilterOperator('Date')],
    },
    {
      headerName: 'Amount',
      field: 'amount.value',
      flex: 1,
      valueGetter: (_, row) => formatCurrency(row.amount),
      filterOperators: [createTextFilterOperator('Amount')],
    },
    {
      headerName: 'Currency',
      field: 'amount.currency',
      flex: 1,
      valueGetter: (_, row) => row.amount?.currency,
      filterOperators: [createTextFilterOperator('Currency')],
    },
    {
      headerName: 'External source',
      field: 'source',
      flex: 1,
      filterOperators: [createSingleSelectFilterOperator('External source', PaymentSource)],
    },
    {
      headerName: 'External reference',
      field: 'billectaExternalReference',
      flex: 1,
      filterOperators: [createTextFilterOperator('External reference')],
    },
    {
      field: 'actions',
      type: 'actions',
      // The default actions column is a fixed 100px regardless of how many actions render;
      // a row can show up to 4 (book, mark-manual, mark-returned, verify-sender), which clips at that width.
      width: 160,
      getActions: ({ row }) =>
        actionFactories.flatMap((factory, index) => {
          const result = factory(row)
          if (!result) return []

          return [
            <Tooltip key={result.tooltip} title={result.tooltip}>
              <span>
                <GridActionsCellItem
                  icon={ACTION_ICONS[index]}
                  label={result.tooltip}
                  disabled={(result as { disabled?: boolean }).disabled}
                  onClick={(event) => result.onClick(event, row)}
                />
              </span>
            </Tooltip>,
          ]
        }),
    },
  ]

  return (
    <>
      <VerifyPaymentModal
        sender={sender}
        onClose={() => setSender(undefined)}
        onApprove={onReturnMoney}
        onWhiteList={handleAddToWhitelist}
      />
      <CoreMyntModal
        wrapperProps={{ sx: { maxWidth: '300px' } }}
        open={Boolean(selectedPayment)}
        title="Add OCR manually"
        onClose={handleModalClose}
        actions={(Wrapper) => (
          <Wrapper>
            <Button onClick={handleModalClose}>Cancel</Button>
            <LoadingButton loading={loading} variant="primary" onClick={form.handleSubmit(handleAddOcr)}>
              Set OCR
            </LoadingButton>
          </Wrapper>
        )}
      >
        <Box sx={{ paddingTop: '8px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <span>Payment extra info:</span>
            <pre
              style={{
                margin: 0,
                backgroundColor: '#eeeeee',
                padding: '8px',
                borderRadius: '8px',
                border: '1px solid #cccccc',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
              }}
            >
              {selectedPayment?.info}
            </pre>
          </Box>
          <TextFieldController control={form.control} name="ocr" labelText="OCR" />
        </Box>
      </CoreMyntModal>
      <TableWrapper>
        <TableHeader title="Payments" apiRef={apiRef} />
        <DataGridPremium
          {...gridProps}
          apiRef={apiRef}
          rows={payments}
          columns={columns}
          headerFilters={config.enablePaymentsFiltering}
          disableColumnFilter
          headerFilterHeight={81}
          getDetailPanelContent={({ row }) => <JSONDetailPanel src={row} />}
          getDetailPanelHeight={() => 'auto'}
          initialState={{
            ...gridProps.initialState,
            filter: {
              filterModel: {
                items: [{ id: 0, field: STATE, operator: 'customArrayContains', value: stateFilter }],
              },
            },
          }}
          onFilterModelChange={(model) => {
            const state = model.items.find((item) => item.field === STATE)
            if (state) setStateFilter(state.value)
          }}
          slots={{
            headerFilterMenu: null,
          }}
          slotProps={{ headerFilterCell: { showClearIcon: true } }}
          sx={{
            ...gridProps.sx,
            '& .MuiDataGrid-columnHeader': { backgroundColor: ColorsDeprecated.surfaceOnWhite },
          }}
        />
      </TableWrapper>
    </>
  )
}

type VerifyPaymentProps = {
  sender: BackOfficePaymentResponseDto | undefined
  onWhiteList: (sender: BackOfficePaymentResponseDto) => Promise<unknown> | void
  onApprove: (sender: BackOfficePaymentResponseDto) => Promise<unknown> | void
  onClose: () => void
}

const VerifyPaymentModal = ({ sender, onWhiteList, onApprove, onClose }: VerifyPaymentProps) => {
  const [isApproveLoading, setIsApproveLoading] = useState(false)
  const [isWhiteListLoading, setIsWhiteListLoading] = useState(false)

  const { data: receiver, isLoading } = usePaymentReceiver(sender?.id, { enabled: Boolean(sender?.id) })

  const handleClose = () => {
    onClose()
    setIsApproveLoading(false)
    setIsWhiteListLoading(false)
  }

  if (!sender) return null

  const handleApprove = () => {
    setIsApproveLoading(true)

    const promiseOrVoid = onApprove(sender)

    if (promiseOrVoid instanceof Promise) {
      setIsApproveLoading(true)

      promiseOrVoid
        .then(() => {
          handleClose()
        })
        .finally(() => {
          setIsApproveLoading(false)
        })
    } else {
      handleClose()
    }
  }

  const handleWhitelist = () => {
    setIsWhiteListLoading(true)

    const promiseOrVoid = onWhiteList(sender)

    if (promiseOrVoid instanceof Promise) {
      setIsWhiteListLoading(true)

      promiseOrVoid
        .then(() => {
          handleClose()
        })
        .finally(() => {
          setIsWhiteListLoading(false)
        })
    } else {
      handleClose()
    }
  }

  return (
    <CoreMyntModal
      actions={(Wrapper) => (
        <Wrapper>
          <LoadingButton loading={isApproveLoading} onClick={handleApprove}>
            Return money
          </LoadingButton>
          <Tooltip title="Future payments payments from this sender to this receiver will be automatically approved. This can be removed">
            <LoadingButton loading={isWhiteListLoading} onClick={handleWhitelist} variant="primary">
              <Box style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <FeatherIcon size={16} icon="info" />
                Add to whitelist
              </Box>
            </LoadingButton>
          </Tooltip>
        </Wrapper>
      )}
      open={!isLoading}
      title="Verify Sender"
      onClose={handleClose}
    >
      <Box style={{ display: 'grid', gridAutoFlow: 'row', gridTemplateColumns: 'auto 1fr 1fr', gap: 8 }}>
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gridColumn: '2 / span 4',
            gap: 8,
          }}
        >
          <Box>Sender</Box>
          <Box>Receiver</Box>
        </Box>
        <Box
          style={{
            display: 'grid',
            gridTemplateRows: '1fr 1fr',
            gridColumn: 1,
            gridRow: '2 / span 2',
            paddingRight: 8,
            textAlign: 'right',
          }}
        >
          <Box>Name</Box>
          <Box style={{ alignSelf: 'flex-end' }}>Regno</Box>
        </Box>
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gridRow: 2,
            gridColumn: '2 / span 4',
            fontWeight: 'bold',
            color: colors.brand.black,
            gap: 8,
          }}
        >
          <Box>{sender.senderInformation.name ?? '-'}</Box>
          <Box>{receiver?.name ?? '-'}</Box>
        </Box>
        <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridRow: 3, gridColumn: '2 / span 4', gap: 8 }}>
          <Box>{sender.senderInformation.number ?? '-'}</Box>
          <Box>{receiver?.number ?? '-'}</Box>
        </Box>
      </Box>
    </CoreMyntModal>
  )
}
